import React, { useState, useEffect } from "react";
import { Icons, SidebarIcons } from "../../../../../utils/icons";
import DefaultButton from "../../../../components/buttons/defaultButton";
import CommonModal from "../../../../components/modals/commonModel";
import { Checkbox } from "flowbite-react";
import DefaultTextarea from "../../../../components/inputs/defaultTextarea";
import DefaultToggleInput from "../../../../components/inputs/defaultToggleInput";
import { getLeaveType } from "../../../../../utils/common";
import { useSelector } from "react-redux";

export default function LeaveDetail({
  currentRecord,
  data,
  handleApprove,
  handleReject,
}) {
  const [modalType, setModalType] = useState(null);
  const userProfile = useSelector((state) => state.user.user);

  const closeModal = () => {
    setModalType(null);
    setRejectionNotes("");
  };

  // local paid map for each day: true=paid, false=not paid
  const [dayPaidState, setDayPaidState] = useState({});
  const [daySelectedState, setDaySelectedState] = useState({});
  const [rejectionNotes, setRejectionNotes] = useState("");

  // initialize when modal opens or currentRecord changes
  useEffect(() => {
    if (modalType === "approve" && currentRecord?.days?.dataArray) {
      const initial = {};
      const selInit = {};
      currentRecord.days.dataArray.forEach((d, idx) => {
        const v = Number(d?.is_paid);
        // map 1->true (paid), others -> false (not paid)
        // Default to paid (true) for all days
        initial[idx] = true; // Always default to paid
        selInit[idx] = false;
      });
      setDayPaidState(initial);
      setDaySelectedState(selInit);
    }
  }, [modalType, currentRecord]);

  const toggleDaySelected = (idx) => {
    setDaySelectedState((prev) => {
      const selected = !prev[idx];
      // when selecting, default switch to Paid (true)
      if (selected) {
        setDayPaidState((p) => ({ ...p, [idx]: true }));
      }
      return { ...prev, [idx]: selected };
    });
  };

  const paidLabel = (val) => (val ? "Paid" : "Not paid");

  // build payload for API with updated is_paid for selected days
  const getUpdatedRecordForApproval = () => {
    const updatedDays = (currentRecord?.days?.dataArray || [])
      .map((d, idx) => {
        // Ensure id is a valid number
        const dayId = d.id ? Number(d.id) : null;
        
        // Skip days without valid id
        if (!dayId || isNaN(dayId)) {
          return null;
        }

        if (daySelectedState[idx]) {
          // For selected days, update is_paid and status
          return { 
            id: dayId,
            date: d.date,
            type: d.type,
            is_paid: dayPaidState[idx] ? 1 : 0, 
            status: 1 
          };
        }
        
        // For unselected days, keep original values
        return {
          id: dayId,
          date: d.date,
          type: d.type,
          is_paid: d.is_paid || 0,
          status: d.status || 0
        };
      })
      .filter(day => day !== null && day.id); // Remove null entries and ensure id exists

    return {
      days: updatedDays,
      status: 1,
    };
  };

   
  // Get initials from name: first letter of first name + first letter of last name
  const getInitials = (name) => {
    if (!name) return "NA";
    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      // First letter of first name + first letter of last name
      return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
    } else if (nameParts.length === 1) {
      // If only one name, take first two letters
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    return "NA";
  };

  const staffName = currentRecord?.name?.data?.name || "";
  const staffImage = currentRecord?.image?.data;
  const initials = getInitials(staffName);

  return (
    <div>
      <div className="flex flex-col gap-4">
        <div className="flex items-center flex-row gap-4 ">
          <div className="rounded-full overflow-hidden w-16 h-16 flex items-center justify-center bg-primary-500 text-white font-semibold text-lg">
            {staffImage ? (
              <img src={staffImage} alt={staffName} className="w-full h-full object-cover" />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <span className="flex flex-col gap-0">
            <p className="text-lg font-semibold text-theme">
              {currentRecord?.name.data.name}
            </p>
            <p className="text-sm text-textLight">
              {currentRecord?.name?.data?.id}
            </p>
          </span>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {data
            .filter((key) => key.label !== "days" && key.label !== "Staff ID")
            .map((data, index) => (
              <div key={index} className="flex flex-col gap-1">
                <p className="text-textLight font-medium">{data.label}</p>
                <p className="text-theme font-medium text-sm">{data.value}</p>
              </div>
            ))}
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-cs text-textLight">Days details</span>
          <div className="flex flex-col gap-1 text-theme">
            {currentRecord?.days?.dataArray.map((day, index) => (
              <div key={index} className="flex flex-row gap-2 items-center">
                <p className="font-medium flex flex-row items-center gap-1">
                  <span className="text-lg">
                    {SidebarIcons.STAFFATTENDANCE}
                  </span>
                  <span className="text-sm">{day.date}</span>
                </p>

                <p className="font-medium flex flex-row items-center gap-1">
                  <span className="w-[5px] h-[5px] bg-theme rounded-full"></span>
                  {getLeaveType(day.type)}
                </p>
                {currentRecord?.status.data === 1 ? (
                  <span>
                    {day.status === 1 ? (
                      ` Approved : -${day.is_paid ? "Paid" : "Unpaid"}`
                    ) : (
                      <p>Rejected</p>
                    )}
                  </span>
                ) : (
                  ""
                )}
              </div>
            ))}
          </div>
        </div>
        {currentRecord?.remarks?.data ? (
          <div className="flex flex-col gap-1">
            <p className="text-textLight font-medium">Remarks</p>
            <p className="text-theme font-medium text-sm">
              {currentRecord?.remarks?.data}
            </p>
          </div>
        ) : (
          ""
        )}

        {userProfile?.type == 1 &&
          currentRecord?.status.data != 1 &&
          currentRecord?.status.data != 2 && (
            <div className="flex flex-row gap-4">
              <DefaultButton
                variant="success"
                minWidth
                name="Approve"
                onClick={() => setModalType("approve")}
              />
              <DefaultButton
                variant="danger"
                minWidth
                name="Reject"
                onClick={() => setModalType("reject")}
              />
            </div>
          )}
        {currentRecord?.status.data === 1 && (
          <div className="bg-success-50 border border-success-200 rounded-lg p-3">
            <p className="text-success-700 font-medium text-sm flex items-center gap-2">
              <span>✓</span> Leave Approved
            </p>
          </div>
        )}
        {currentRecord?.status.data === 2 && (
          <div className="bg-danger-50 border border-danger-200 rounded-lg p-3">
            <p className="text-danger-700 font-medium text-sm flex items-center gap-2">
              <span>✗</span> Leave Rejected
            </p>
            {currentRecord?.remarks?.data && (
              <p className="text-danger-600 text-xs mt-1">
                Reason: {currentRecord.remarks.data}
              </p>
            )}
          </div>
        )}
      </div>

      <CommonModal
        modalSize="lg"
        headerTitle={
          modalType === "approve" ? "Leave Approval" : "Leave Rejection"
        }
        onClose={closeModal}
        show={!!modalType}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center flex-row gap-4 ">
            <div className="rounded-full overflow-hidden w-16 h-16 flex items-center justify-center bg-primary-500 text-white font-semibold text-lg">
              {staffImage ? (
                <img src={staffImage} alt={staffName} className="w-full h-full object-cover" />
              ) : (
                <span>{initials}</span>
              )}
            </div>
            <span className="flex flex-col gap-0">
              <p className="text-lg font-semibold text-theme">
                {currentRecord?.name.data.name}
              </p>
              <p className="text-sm text-textLight">
                {currentRecord?.name?.data?.id}
              </p>
            </span>
          </div>

          {/* Shared details */}
          <div className="grid grid-cols-3 gap-4">
            {data
              .filter((key) => key.label !== "days" && key.label !== "Staff ID")
              .map((d, index) => (
                <div key={index} className="flex flex-col text-cs">
                  <p className="text-textLight font-medium">{d.label}</p>
                  <p className="text-theme font-medium text-sm">{d.value}</p>
                </div>
              ))}
          </div>

          {modalType === "approve" && (
            <>
              <div className="flex flex-col gap-2">
                <span className="text-cs text-textLight">Days details</span>
                <div className="flex flex-col gap-2 text-theme text-sm">
                  <div className="flex flex-col gap-1 text-theme">
                    {currentRecord?.days?.dataArray?.map((day, index) => (
                      <div
                        key={index}
                        className="flex flex-row items-center justify-between gap-2"
                      >
                        <div className="flex flex-row items-center gap-2">
                          <Checkbox
                            checked={!!daySelectedState[index]}
                            onChange={() => toggleDaySelected(index)}
                          />
                          <p className="font-medium flex flex-row items-center gap-1">
                            <span className="text-lg">
                              {SidebarIcons.STAFFATTENDANCE}
                            </span>
                            <span className="text-sm">{day.date}</span>
                          </p>
                          <p className="font-medium flex flex-row items-center gap-1">
                            <span className="w-[5px] h-[5px] bg-theme rounded-full"></span>
                            {getLeaveType(day.type)}
                          </p>
                        </div>
                        <div className="flex items-center justify-end w-48">
                          <div
                            className={
                              daySelectedState[index]
                                ? "flex items-center gap-2"
                                : "opacity-0 pointer-events-none flex items-center gap-2"
                            }
                          >
                            <span className="inline-block w-20 text-right text-xs text-theme font-medium">
                              {paidLabel(!!dayPaidState[index])}
                            </span>
                            <DefaultToggleInput
                              headLabel={""}
                              label={""}
                              isChecked={!!dayPaidState[index]}
                              onChange={() =>
                                setDayPaidState((prev) => ({
                                  ...prev,
                                  [index]: !prev[index],
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <span className="flex flex-row items-center gap-1">
                    {Icons.INFORMATION}
                    <p className="text-theme text-sm font-light">
                      Rest will be unpaid leave.
                    </p>
                  </span>
                </div>
              </div>
            </>
          )}

          {modalType === "reject" && (
            <>
              <div className="flex flex-col gap-2">
                <DefaultTextarea
                  label={"Notes"}
                  value={rejectionNotes}
                  onChange={(e) => setRejectionNotes(e.target.value)}
                  placeholder="Enter rejection notes..."
                />
              </div>
            </>
          )}

          <div className="flex gap-4">
            <DefaultButton
              variant={modalType === "approve" ? "success" : "danger"}
              minWidth
              name={modalType === "approve" ? "Approve" : "Reject"}
              onClick={() => {
                if (modalType === "approve") {
                  if (!handleApprove) {
                    console.error("handleApprove function is not provided");
                    return;
                  }
                  const payload = getUpdatedRecordForApproval();
                  // Use uuid if available, otherwise fallback to id
                  const leaveId =   currentRecord?.id?.data;
                  handleApprove(leaveId, payload);
                  closeModal();
                } else if (modalType === "reject") {
                  if (!handleReject) {
                    console.error("handleReject function is not provided");
                    return;
                  }
                  if (!rejectionNotes.trim()) {
                    alert("Please provide rejection notes");
                    return;
                  }
                  // Use uuid if available, otherwise fallback to id
                  const leaveId =   currentRecord?.id?.data;
                  handleReject(leaveId, rejectionNotes);
                  closeModal();
                }
              }}
              disabled={modalType === "reject" && !rejectionNotes.trim()}
            />
            <DefaultButton
              variant="secondary"
              minWidth
              name="Cancel"
              onClick={closeModal}
            />
          </div>
        </div>
      </CommonModal>
    </div>
  );
}
